import { type ChangeEvent, useDeferredValue, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  FileSearch,
  FileSpreadsheet,
  FileUp,
  Loader2,
  Map,
  RotateCcw,
  Search,
  Sparkles,
  X,
} from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { applyQuoteChange, quoteQueryOptions } from '../api/queries'
import type { Quote } from '../api/types'
import { AiData } from '../ui/ai-data'
import { SurfaceCard } from '../ui/page'
import {
  createImport,
  createImportFromQuoteFile,
  importErrorMessage,
  insertImport,
  readImport,
  readImportFile,
  searchCatalog,
  streamImportExtraction,
} from './api'
import { AgentCustomItem, AgentPassNote, AgentPassProgress, AgentRowStatus } from './agent-pass'
import { agentMatches, useAgentPass } from './agent-pass-state'
import { LiveImport, MatchCard } from './live-import'
import { applyImportEvent, emptyLiveImport, type LiveImportState } from './live-import-state'
import {
  customDecisionChoices,
  decideRow,
  decisionChoices,
  defaultCandidate,
  documentWarnings,
  type ReviewDecision,
} from './review'
import { RowSuggestions } from './row-suggestions'
import { restoreReview, rowEntries, useReviewSaver, type ReviewState } from './review-save'
import type {
  AlternativeStrategy,
  CatalogMatch,
  DocumentImport,
  ImportMode,
  ImportReview,
  ImportReviewUpdate,
  ImportSelection,
  MatchCandidate,
  ReviewedRow,
} from './types'
import { SearchInput } from '@/components/search-input'

// Import equipment from a document (demo `features/imports/import-workspace.tsx`): upload, read
// live, review every row and its catalog match, then add the approved rows to the quote. Every
// review choice is saved as it is made, so an import reopens exactly as it was left (USA-1337).

/** A file already in the quote's Files library (the Files owner's read model). */
export type ImportSourceFile = { id: string; original_file_name: string }

type MatchStrategy = NonNullable<ImportReview['match_mode']>

const alternativeChoices: Array<{ value: AlternativeStrategy; label: string }> = [
  { value: 'preferred_alternative', label: 'Preferred' },
  { value: 'budget_alternative', label: 'Budget' },
  { value: 'faster_alternative', label: 'Faster lead time' },
]

type ModeConfig = {
  title: string
  accept: string
  uploadHelp: string
  icon: typeof FileUp
  strategies: Array<{ value: MatchStrategy; label: string }>
}

const modeConfig: Record<ImportMode, ModeConfig> = {
  equipment_schedule: {
    title: 'Equipment schedule',
    accept: '.xlsx,.csv,.pdf,.aqproj,image/png,image/jpeg',
    uploadHelp: 'PDF, CAD sheet, XLSX, CSV, AutoQuotes, PNG or JPG up to 25 MB',
    icon: FileSpreadsheet,
    strategies: [
      { value: 'catalog_match', label: 'Catalog match' },
      { value: 'exact', label: 'Exact matches only' },
    ],
  },
  competitor_quote: {
    title: 'Competitor quote',
    accept: '.pdf,.xlsx,.csv,image/png,image/jpeg',
    uploadHelp: 'PDF, XLSX, CSV, PNG or JPG up to 25 MB',
    icon: FileSearch,
    strategies: [
      { value: 'auto_detect', label: 'Best match' },
      { value: 'exact', label: 'Exact matches only' },
    ],
  },
  floor_plan: {
    title: 'Floor plan',
    accept: '.pdf,image/png,image/jpeg',
    uploadHelp: 'PDF, PNG or JPG up to 25 MB',
    icon: Map,
    strategies: [
      { value: 'catalog_match', label: 'Match symbols to catalog' },
      { value: 'dimensions_only', label: 'Dimensions only' },
    ],
  },
}

function candidateForStrategy(row: ReviewedRow, strategy: MatchStrategy) {
  if (strategy === 'dimensions_only') return undefined
  return defaultCandidate(row.candidates, strategy === 'exact')
}

function confidenceTone(value: string) {
  const confidence = Number(value)
  if (confidence >= 0.9) return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
  if (confidence >= 0.75) return 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
  return 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
}

function Percent({ value, prefix }: { value: string; prefix?: string }) {
  return (
    <span className={cn('rounded-full px-1.5 py-px text-[11px] font-medium tabular-nums', confidenceTone(value))}>
      {prefix}
      {Math.round(Number(value) * 100)}%
    </span>
  )
}

function withoutRow<T>(values: Record<string, T>, rowId: string) {
  const next = { ...values }
  delete next[rowId]
  return next
}

function candidateLabel(candidate: MatchCandidate) {
  const kind =
    candidate.match_type === 'exact'
      ? 'Exact'
      : candidate.match_type === 'close_match'
        ? 'Close match'
        : candidate.match_type === 'ai_suggested'
          ? 'AI suggested'
          : 'Alternative'
  return `${candidate.manufacturer} ${candidate.model} · ${kind}`
}

function ReviewRow({
  importId,
  row,
  decision,
  selectedProductId,
  warningResolved,
  manualMatch,
  configuration,
  accessories,
  canInclude,
  disabled,
  onDecide,
  onMatch,
  onResolve,
  onManualMatch,
  onConfigure,
  onToggleAccessory,
}: {
  importId: string
  row: ReviewedRow
  decision?: ReviewDecision
  selectedProductId?: number
  warningResolved: boolean
  manualMatch?: CatalogMatch
  configuration: Record<string, string>
  accessories: number[]
  canInclude: boolean
  disabled: boolean
  onDecide: (decision: ReviewDecision) => void
  onMatch: (productId: number) => void
  onResolve: () => void
  onManualMatch: (match: CatalogMatch) => void
  onConfigure: (configuration: Record<string, string>) => void
  onToggleAccessory: (productId: number) => void
}) {
  const api = useQuoteBuilderApi()
  const [correcting, setCorrecting] = useState(false)
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query.trim())
  const search = useQuery({
    queryKey: ['import-catalog-search', deferredQuery],
    queryFn: ({ signal }) => searchCatalog(api, deferredQuery, signal),
    enabled: correcting && deferredQuery.length >= 2,
    staleTime: 60_000,
  })
  const selected = row.candidates.find((candidate) => candidate.product_id === selectedProductId)
  const selectedIdentity = selected ?? manualMatch
  const needsReview =
    !selectedIdentity ||
    Number(row.confidence) < 0.85 ||
    (selected && Number(selected.confidence) < 0.85) ||
    ((row.warnings ?? []).length > 0 && !warningResolved)
  // One recommendation needs no picker; the card says it all.
  const choosable = !selected || row.candidates.length + (manualMatch ? 1 : 0) > 1
  // Close matches alone are offered one click each; the reviewer chooses (USA-1339).
  const closeMatches =
    !selectedIdentity && row.candidates.every((candidate) => candidate.match_type === 'close_match')
      ? row.candidates
      : []
  const results = search.data ?? []

  return (
    <article
      className={cn('border-b px-4 py-3 last:border-b-0', decision === 'rejected' && 'bg-muted/50', row.parent_row_id && 'ml-6 border-l')}
      data-row-id={row.row_id}
    >
      <fieldset className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(240px,0.6fr)]" disabled={disabled}>
        <div className="min-w-0 space-y-3">
          <div className={cn('min-w-0', decision === 'rejected' && 'opacity-60')}>
            <div className="flex flex-wrap items-center gap-2">
              {row.symbol ? (
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] font-medium text-muted-foreground">
                  {row.symbol}
                </span>
              ) : null}
              <p className="text-sm font-semibold">
                {row.manufacturer ?? 'No manufacturer'} {row.model ?? ''}
              </p>
              <Percent value={row.confidence} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{row.description}</p>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Qty {Number(row.quantity)}
              {row.zone ? ` · ${row.zone}` : ''}
              {row.dimensions ? ` · ${row.dimensions}` : ''}
            </p>
          </div>

          <div className="min-w-0">
            <p className="mb-1.5 flex flex-wrap items-center gap-x-2 text-xs font-medium text-muted-foreground">
              {closeMatches.length ? 'Close matches' : 'Recommended match'}
              <AgentRowStatus row={row} />
            </p>
            {closeMatches.length ? (
              <ul aria-label={`Close matches for ${row.description}`} className="max-w-xl divide-y rounded-md border">
                {closeMatches.map((candidate) => (
                  <li className="flex items-center gap-2 px-2.5 py-2" key={candidate.product_id}>
                    <MatchCard candidate={candidate} className="flex-1" labelled={false} printedModel={row.model} />
                    <Button
                      aria-label={`Choose ${candidate.manufacturer} ${candidate.model}`}
                      className="shrink-0"
                      onClick={() => onMatch(candidate.product_id)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      Choose
                    </Button>
                  </li>
                ))}
              </ul>
            ) : row.candidates.length || manualMatch ? (
              <AiData className="max-w-xl" label="AI-assisted catalog recommendation">
                {selected ? (
                  <MatchCard
                    candidate={selected}
                    className={cn('px-2.5 py-2', choosable && 'border-b border-emerald-200 dark:border-emerald-900')}
                    printedModel={row.model}
                  />
                ) : null}
                {choosable ? (
                  <Select onValueChange={(value) => onMatch(Number(value))} value={selectedProductId ? String(selectedProductId) : undefined}>
                    <SelectTrigger aria-label={`Match for ${row.description}`} className="h-9 w-full border-0 bg-transparent shadow-none">
                      <SelectValue placeholder="Choose a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {manualMatch ? (
                        <SelectItem value={String(manualMatch.id)}>
                          {manualMatch.manufacturer} {manualMatch.model} · Chosen
                        </SelectItem>
                      ) : null}
                      {row.candidates.map((candidate) => (
                        <SelectItem key={candidate.product_id} value={String(candidate.product_id)}>
                          {candidateLabel(candidate)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
              </AiData>
            ) : row.agent?.custom_reason ? (
              <AgentCustomItem reason={row.agent.custom_reason} />
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                No catalog match
              </div>
            )}
            {selected && (selected.reason || selected.match_type !== 'exact') ? (
              <div
                className={cn(
                  'mt-2 flex gap-2 text-xs text-muted-foreground',
                  selected.match_type === 'ai_suggested' ? 'items-start' : 'flex-wrap items-center',
                )}
              >
                {selected.match_type === 'exact' ? (
                  <CheckCircle2 aria-hidden="true" className="size-3.5 text-emerald-600" />
                ) : selected.match_type === 'ai_suggested' ? (
                  <Sparkles aria-label="SmartQuote" className="mt-px size-3.5 shrink-0 text-emerald-700 dark:text-emerald-400" role="img" />
                ) : (
                  <Percent prefix="Match " value={selected.confidence} />
                )}
                <span>{selected.reason ?? selected.evidence.join(' · ')}</span>
              </div>
            ) : null}
            <div aria-label={`Alternatives for ${row.description}`} className="mt-2 flex flex-wrap gap-1" role="group">
              {alternativeChoices.map((choice) => (
                <Button disabled key={choice.value} size="sm" type="button" variant="outline">
                  {choice.label}
                </Button>
              ))}
            </div>
            {selectedIdentity && selectedProductId ? (
              <RowSuggestions
                accessories={accessories}
                candidate={selected}
                configuration={configuration}
                disabled={disabled}
                importId={importId}
                key={selectedProductId}
                onConfigure={onConfigure}
                onToggleAccessory={onToggleAccessory}
                product={{
                  id: selectedProductId,
                  manufacturer: selectedIdentity.manufacturer,
                  model: selectedIdentity.model,
                  title: selectedIdentity.title,
                }}
                row={row}
              />
            ) : null}
            <Button
              className="mt-1 h-8 px-1.5"
              onClick={() => setCorrecting((current) => !current)}
              size="sm"
              type="button"
              variant="link"
            >
              <Search aria-hidden="true" className="size-3.5" />
              {correcting ? 'Close catalog search' : selectedIdentity ? 'Choose another product' : 'Find a catalog product'}
            </Button>
            {correcting ? (
              <div className="mt-1 rounded-lg border bg-muted/40 p-2.5">
                <label className="relative block">
                  <span className="sr-only">Search the catalog</span>
                  <Search aria-hidden="true" className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <SearchInput
                    autoFocus
                    className="h-9 bg-background pl-8"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Manufacturer, model or equipment"
                    value={query}
                  />
                </label>
                <div className="mt-2 space-y-1">
                  {search.isFetching ? <p className="px-2 py-2 text-xs text-muted-foreground">Searching the catalog…</p> : null}
                  {!search.isFetching && deferredQuery.length >= 2 && !results.length && !search.isError ? (
                    <p className="px-2 py-2 text-xs text-muted-foreground">No catalog products found.</p>
                  ) : null}
                  {search.isError ? (
                    <p className="px-2 py-2 text-xs font-medium text-destructive">The catalog could not be searched. Try again.</p>
                  ) : null}
                  {results.map((product) => (
                    <button
                      className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-2 text-left outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      key={product.id}
                      onClick={() => {
                        onManualMatch(product)
                        setCorrecting(false)
                      }}
                      type="button"
                    >
                      <span className="text-sm font-semibold">
                        {product.manufacturer} {product.model}
                      </span>
                      <span className="line-clamp-1 text-xs text-muted-foreground">{product.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="min-w-0 lg:border-l lg:pl-4">
          <ReviewChoiceGroup
            className="mb-3"
            disabled={disabled || !canInclude}
            label={`Review ${row.description}`}
            onChange={onDecide}
            options={!canInclude ? decisionChoices.slice(1) : selectedProductId ? decisionChoices : customDecisionChoices}
            value={decision ?? null}
          />
          {(row.requirement_notes ?? []).length ? (
            <div className="mb-3 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">From the document</p>
              {(row.requirement_notes ?? []).map((note) => (
                <p className="rounded-md border bg-muted/40 px-2.5 py-2 text-xs" key={note}>
                  {note}
                </p>
              ))}
            </div>
          ) : null}
          <details>
            <summary className="flex cursor-pointer items-center justify-between gap-2 text-xs font-medium text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <ChevronDown aria-hidden="true" className="size-3" />
                Source
              </span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[11px] font-medium',
                  needsReview
                    ? 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                    : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
                )}
              >
                {needsReview ? 'Needs review' : 'Ready'}
              </span>
            </summary>
            <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
              {(row.evidence ?? []).map((evidence) => (
                <li key={evidence}>{evidence}</li>
              ))}
            </ul>
          </details>
          {(row.warnings ?? []).length ? (
            <Button
              className={cn('mt-2 h-auto min-h-8 whitespace-normal px-1.5 text-left', warningResolved ? 'text-emerald-700' : 'text-amber-700')}
              onClick={onResolve}
              size="sm"
              type="button"
              variant="ghost"
            >
              {warningResolved ? <Check aria-hidden="true" className="size-3.5" /> : <AlertTriangle aria-hidden="true" className="size-3.5" />}
              {warningResolved ? 'Warning reviewed' : (row.warnings ?? []).join(' · ')}
            </Button>
          ) : null}
        </div>
      </fieldset>
    </article>
  )
}

function SourcePreview({ file }: { file: File }) {
  const url = useMemo(() => URL.createObjectURL(file), [file])
  useEffect(() => () => URL.revokeObjectURL(url), [url])
  if (file.type.startsWith('image/')) return <img alt={file.name} className="mx-auto max-h-[70vh] w-auto" src={url} />
  if (file.type === 'application/pdf') return <iframe className="h-[70vh] w-full rounded-md border" src={url} title={file.name} />
  return <p className="py-8 text-center text-sm text-muted-foreground">{file.name}</p>
}

export function ImportWorkspace({
  quote,
  onQuoteChange,
  onClose,
  initialQuoteFile,
  initialImportId,
  onImportChange,
}: {
  quote: Quote
  onQuoteChange: (quote: Quote) => void
  onClose: () => void
  initialQuoteFile?: ImportSourceFile
  /** Reopen this saved import as it was left. */
  initialImportId?: string
  /** The open import changed, so the page address can follow it. */
  onImportChange?: (importId: string | null) => void
}) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const navigate = useNavigate()
  const viewId = useId()
  const fileInput = useRef<HTMLInputElement>(null)
  const inFlight = useRef(false)
  const [mode, setMode] = useState<ImportMode>('equipment_schedule')
  const config = modeConfig[mode]
  const [strategy, setStrategy] = useState<MatchStrategy>(config.strategies[0]!.value)
  const [uploaded, setUploaded] = useState<File | null>(null)
  const [showSource, setShowSource] = useState(false)
  const [documentImport, setDocumentImport] = useState<DocumentImport | null>(null)
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({})
  const [matches, setMatches] = useState<Record<string, number>>({})
  const [manualMatches, setManualMatches] = useState<Record<string, CatalogMatch>>({})
  // Options chosen and accessories added in review, per row; they belong to the row's current product.
  const [rowOptions, setRowOptions] = useState<Record<string, Record<string, string>>>({})
  const [rowAccessories, setRowAccessories] = useState<Record<string, number[]>>({})
  const [resolvedWarnings, setResolvedWarnings] = useState<Record<string, boolean>>({})
  const [globalWarningsResolved, setGlobalWarningsResolved] = useState(false)
  const [busy, setBusy] = useState<'upload' | 'insert' | null>(null)
  const [pendingSelections, setPendingSelections] = useState<ImportSelection[] | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [rowQuery, setRowQuery] = useState('')
  const [visibleRows, setVisibleRows] = useState(12)
  const [live, setLive] = useState<LiveImportState | null>(null)
  const saver = useReviewSaver(api)
  // Rows Sales chose for in this visit: the agent's later proposals never replace those choices.
  const touched = useRef(new Set<string>())
  const resuming = Boolean(initialImportId) && !documentImport && !error
  const sourceName =
    uploaded?.name ??
    initialQuoteFile?.original_file_name ??
    documentImport?.original_file_name ??
    (resuming ? 'saved review' : undefined)

  function prepareReview(item: DocumentImport, selectedStrategy: MatchStrategy) {
    const rows = item.extraction_result?.rows ?? []
    const review = item.review
    const reviewStrategy = review?.match_mode ?? selectedStrategy
    const restored = restoreReview(
      rows,
      review,
      Object.fromEntries(
        rows.flatMap((row) => {
          const candidate = candidateForStrategy(row, reviewStrategy)
          return candidate ? [[row.row_id, candidate.product_id]] : []
        }),
      ),
    )
    setDecisions(restored.decisions)
    setMatches(restored.matches)
    setManualMatches(restored.manualMatches)
    setRowOptions(restored.rowOptions)
    setRowAccessories(restored.rowAccessories)
    setRowQuery('')
    setVisibleRows(12)
    setResolvedWarnings(restored.resolvedWarnings)
    setGlobalWarningsResolved(Boolean(review?.warnings_reviewed) || (item.extraction_result?.warnings.length ?? 0) === 0)
    if (item.mode !== 'aqproj') setMode(item.mode)
    setStrategy(reviewStrategy)
    setDocumentImport(item)
    // A reopened review starts each untouched row from the same match mode.
    if (!review?.match_mode && item.status === 'ready_for_review') saver.save(item.id, { match_mode: reviewStrategy })
  }

  async function openImport(start: () => Promise<DocumentImport>, fileName?: string) {
    if (inFlight.current) return
    inFlight.current = true
    setBusy('upload')
    setError('')
    setSuccess('')
    // The live read is the thing to watch; the document stays one tap away.
    setShowSource(false)
    if (fileName) setLive(emptyLiveImport(fileName))
    try {
      const opened = await start()
      onImportChange?.(opened.id)
      let item = opened
      // A read keeps going on the server if the page is left; asking again waits for it.
      if (opened.status === 'awaiting_upload') {
        const name = opened.original_file_name
        setLive((current) => current ?? emptyLiveImport(name))
        item = await streamImportExtraction(api, opened.id, (event) =>
          setLive((current) => applyImportEvent(current ?? emptyLiveImport(name), event)),
        )
      }
      prepareReview(item, strategy)
    } catch (reason) {
      setError(importErrorMessage(reason, 'Could not read this file. Check your connection and try again.'))
    } finally {
      inFlight.current = false
      setBusy(null)
      setLive(null)
    }
  }

  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    if (initialImportId) {
      opened.current = true
      void openImport(async () => {
        const item = await readImport(api, initialImportId)
        // The document view comes back with the review; the review never waits on it.
        void readImportFile(api, item).then(setUploaded, () => undefined)
        return item
      })
    } else if (initialQuoteFile) {
      opened.current = true
      void openImport(
        () => createImportFromQuoteFile(api, initialQuoteFile.id, quote.id, mode),
        initialQuoteFile.original_file_name,
      )
    }
    // Opens the saved import, or reads the library file, once, when the workspace opens on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialImportId, initialQuoteFile])

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (fileInput.current) fileInput.current.value = ''
    if (!file) return
    if (file.size > 25 * 1024 * 1024) {
      setError('Choose a file smaller than 25 MB.')
      return
    }
    setUploaded(file)
    void openImport(() => createImport(api, file, mode, quote.id), file.name)
  }

  const rows = documentImport?.extraction_result?.rows ?? []
  const canInsert = strategy !== 'dimensions_only'
  const warnings = documentWarnings(documentImport?.extraction_result?.warnings ?? [], rows, matches)
  const warningsReviewed = globalWarningsResolved || !warnings.length

  async function handleInsert() {
    if (!documentImport || documentImport.status === 'inserted' || inFlight.current) return
    if (rows.some((row) => !decisions[row.row_id]) || !warningsReviewed) {
      setError('Approve or reject every item before importing.')
      return
    }
    // An approved row without a product becomes a custom line.
    const selections =
      pendingSelections ??
      rows.flatMap((row): ImportSelection[] => {
        if (decisions[row.row_id] !== 'approved') return []
        const productId = matches[row.row_id]
        if (!productId) return [{ row_id: row.row_id }]
        const configuration = rowOptions[row.row_id]
        const accessories = rowAccessories[row.row_id]
        return [
          {
            row_id: row.row_id,
            product_id: productId,
            ...(configuration ? { configuration } : {}),
            ...(accessories?.length ? { accessory_product_ids: accessories } : {}),
          },
        ]
      })
    if (!selections.length) {
      setError('Approve at least one item.')
      return
    }
    inFlight.current = true
    setBusy('insert')
    setError('')
    try {
      const latest = await client.fetchQuery({ ...quoteQueryOptions(api, quote.id), staleTime: 0 })
      setPendingSelections(selections)
      const updated = await insertImport(api, documentImport, latest, selections)
      applyQuoteChange(client, quote.id, updated)
      onQuoteChange(updated)
      setSuccess(`${selections.length} item${selections.length === 1 ? '' : 's'} added to Equipment.`)
      setDocumentImport((current) => (current ? { ...current, status: 'inserted' } : current))
      void navigate({ to: '/quotes/$quoteId/workflow/$stage', params: { quoteId: quote.id, stage: 'pricing' } })
    } catch (reason) {
      setPendingSelections(null)
      setError(importErrorMessage(reason, 'The items could not be added. Try again; nothing is added twice.'))
    } finally {
      inFlight.current = false
      setBusy(null)
    }
  }

  const included = Object.fromEntries(rows.map((row) => [row.row_id, decisions[row.row_id] === 'approved']))
  const includedCount = rows.filter((row) => included[row.row_id]).length
  const decidedCount = rows.filter((row) => decisions[row.row_id]).length
  const reviewComplete = rows.length > 0 && decidedCount === rows.length && warningsReviewed
  /** Save the named rows' choices as `next` leaves them, plus any document-level change. */
  const persist = (rowIds: Iterable<string>, next: Partial<ReviewState> = {}, update: Omit<ImportReviewUpdate, 'rows'> = {}) => {
    if (documentImport?.status !== 'ready_for_review') return
    rowIds = [...rowIds]
    for (const rowId of rowIds) touched.current.add(rowId)
    const state = { decisions, matches, manualMatches, resolvedWarnings, rowOptions, rowAccessories, ...next }
    saver.save(documentImport.id, { rows: rowEntries(state, rowIds), ...update })
  }
  const decide = (row: ReviewedRow, decision: ReviewDecision) => {
    const nextDecisions = decideRow(decisions, row, rows, decision)
    const nextResolved = decision === 'approved' ? { ...resolvedWarnings, [row.row_id]: true } : resolvedWarnings
    setDecisions(nextDecisions)
    setResolvedWarnings(nextResolved)
    persist(
      rows.flatMap((item) =>
        item.row_id === row.row_id || nextDecisions[item.row_id] !== decisions[item.row_id] ? [item.row_id] : [],
      ),
      { decisions: nextDecisions, resolvedWarnings: nextResolved },
    )
  }
  /** A new product for the row drops the options and accessories chosen for the old one. */
  const chooseProduct = (rowId: string, productId: number, next: Partial<ReviewState> = {}) => {
    const changed = matches[rowId] !== productId
    const nextMatches = changed ? { ...matches, [rowId]: productId } : matches
    const nextOptions = changed ? withoutRow(rowOptions, rowId) : rowOptions
    const nextAccessories = changed ? withoutRow(rowAccessories, rowId) : rowAccessories
    if (changed) {
      setMatches(nextMatches)
      setRowOptions(nextOptions)
      setRowAccessories(nextAccessories)
    }
    if (changed || Object.keys(next).length) {
      persist([rowId], { ...next, matches: nextMatches, rowOptions: nextOptions, rowAccessories: nextAccessories })
    }
  }
  const decideAll = (decision: ReviewDecision) => {
    const nextDecisions: Record<string, ReviewDecision> = Object.fromEntries(
      rows.map((row) => [row.row_id, decision === 'approved' && canInsert ? 'approved' : 'rejected']),
    )
    const nextResolved = decision === 'approved' ? Object.fromEntries(rows.map((row) => [row.row_id, true])) : resolvedWarnings
    setDecisions(nextDecisions)
    setResolvedWarnings(nextResolved)
    setGlobalWarningsResolved(true)
    persist(
      rows.map((row) => row.row_id),
      { decisions: nextDecisions, resolvedWarnings: nextResolved },
      { warnings_reviewed: true },
    )
  }
  useAgentPass(documentImport, (item) => {
    if (item.status !== 'ready_for_review' || item.id !== documentImport?.id) return
    setDocumentImport((current) => (current?.id === item.id ? { ...current, extraction_result: item.extraction_result } : current))
    setMatches((current) =>
      agentMatches(
        item.extraction_result?.rows ?? [],
        item.review?.rows ?? {},
        current,
        touched.current,
        (row) => candidateForStrategy(row, strategy)?.product_id,
      ),
    )
  })
  const normalizedRowQuery = rowQuery.trim().toLocaleLowerCase()
  const reviewRows = documentImport?.status === 'inserted' ? rows.filter((row) => included[row.row_id]) : rows
  const filteredRows = normalizedRowQuery
    ? reviewRows.filter((row) =>
        [row.symbol, row.manufacturer, row.model, row.description].some((value) =>
          value?.toLocaleLowerCase().includes(normalizedRowQuery),
        ),
      )
    : reviewRows
  const displayedRows = filteredRows.slice(0, visibleRows)
  const locked = busy !== null || pendingSelections !== null
  const Icon = config.icon
  // A reopened import gets its document back after the review shows; its switch is there from the start.
  const hasSource = Boolean(uploaded) || (Boolean(initialImportId) && !initialQuoteFile)
  const reviewing = documentImport?.status === 'ready_for_review'

  return (
    <section aria-label="Import equipment" className="min-w-0">
      <div className="mb-3 flex min-h-9 flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="sr-only text-base font-semibold sm:not-sr-only">Import equipment</h2>
        {hasSource ? (
          <div aria-label="Import view" className="inline-flex rounded-lg border bg-muted p-0.5" role="group">
            {[
              { value: true, label: 'Document' },
              { value: false, label: 'Items' },
            ].map((view) => (
              <button
                aria-controls={`${viewId}-${view.value ? 'document' : 'items'}`}
                aria-pressed={showSource === view.value}
                className={cn(
                  'min-h-7 rounded-md px-3 text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50',
                  showSource === view.value ? 'bg-background shadow-xs' : 'text-muted-foreground hover:text-foreground',
                )}
                disabled={view.value && !uploaded}
                key={view.label}
                onClick={() => setShowSource(view.value)}
                type="button"
              >
                {view.label}
              </button>
            ))}
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {reviewing && !initialQuoteFile ? (
            <Button
              disabled={locked}
              onClick={() => {
                setUploaded(null)
                setDocumentImport(null)
                setSuccess('')
                setError('')
                onImportChange?.(null)
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <RotateCcw aria-hidden="true" />
              <span className="max-sm:sr-only">New file</span>
            </Button>
          ) : null}
          <Button disabled={busy === 'insert' && documentImport?.status !== 'inserted'} onClick={onClose} size="sm" type="button" variant="outline">
            <ArrowLeft aria-hidden="true" />
            Back <span className="max-lg:sr-only">{initialQuoteFile ? 'to customer' : 'to equipment'}</span>
          </Button>
        </div>
      </div>

      {error ? (
        <div className="mb-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm font-medium text-destructive" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300" role="status">
          <CheckCircle2 aria-hidden="true" className="size-4" />
          {success}
        </div>
      ) : null}

      <div className={cn('grid items-start gap-4', !sourceName && 'xl:grid-cols-[minmax(0,1fr)_300px]')}>
        {uploaded ? (
          <SurfaceCard hidden={!showSource} id={`${viewId}-document`} title={uploaded.name}>
            <div className="p-3">
              <SourcePreview file={uploaded} />
            </div>
          </SurfaceCard>
        ) : null}
        <div className="min-w-0 space-y-4" hidden={hasSource && showSource} id={`${viewId}-items`}>
          {!documentImport && live ? (
            <LiveImport state={live} />
          ) : !documentImport ? (
            <SurfaceCard>
              <div className="grid min-h-64 place-items-center px-5 py-10">
                <div className="w-full max-w-md text-center">
                  <span className="mx-auto grid size-12 place-items-center rounded-xl border bg-muted text-muted-foreground">
                    <Icon aria-hidden="true" className="size-5" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold">{sourceName ? 'Reading equipment' : config.title}</h3>
                  <div className="mt-5 flex justify-center">
                    <input
                      accept={config.accept}
                      aria-label="Equipment document"
                      className="sr-only"
                      onChange={handleFile}
                      ref={fileInput}
                      type="file"
                    />
                    {(initialQuoteFile || resuming) && !error ? (
                      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
                        <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
                        Opening {initialQuoteFile?.original_file_name ?? 'saved review'}
                      </p>
                    ) : (
                      <LoadingButton loading={busy === 'upload'} loadingText="Uploading…" onClick={() => fileInput.current?.click()} type="button">
                        <FileUp aria-hidden="true" />
                        Choose file
                      </LoadingButton>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">{config.uploadHelp}</p>
                </div>
              </div>
            </SurfaceCard>
          ) : (
            <SurfaceCard>
              <div className="sticky top-0 z-10 border-b bg-card">
                {documentImport.status !== 'inserted' ? (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
                    <p className="text-sm text-muted-foreground" role="status">
                      <strong className={cn('font-semibold tabular-nums', reviewComplete ? 'text-emerald-700' : 'text-amber-700')}>
                        {decidedCount} of {rows.length}
                      </strong>{' '}
                      reviewed
                      <AgentPassNote rows={rows} run={documentImport.extraction_result?.agent} />
                    </p>
                    <div className="ml-auto flex items-center gap-1 max-sm:w-full max-sm:justify-end max-sm:[&_button:not(:last-child)_svg]:hidden">
                      <Button disabled={locked} onClick={() => decideAll('rejected')} size="sm" type="button" variant="ghost">
                        <X aria-hidden="true" />
                        Reject all
                      </Button>
                      <Button
                        disabled={locked || !canInsert || !rows.length}
                        onClick={() => decideAll('approved')}
                        size="sm"
                        type="button"
                        variant="ghost"
                      >
                        <CheckCircle2 aria-hidden="true" className="text-emerald-600" />
                        Approve all
                      </Button>
                      <LoadingButton
                        className="ml-1"
                        disabled={busy !== null || !reviewComplete || !includedCount || !canInsert}
                        loading={busy === 'insert'}
                        loadingText="Importing…"
                        onClick={() => void handleInsert()}
                        size="sm"
                        type="button"
                      >
                        <Check aria-hidden="true" />
                        {canInsert ? 'Import' : 'Dimensions review only'}
                      </LoadingButton>
                    </div>
                  </div>
                ) : (
                  <h3 className="px-4 py-2.5 text-sm font-semibold">Added equipment</h3>
                )}
                {documentImport.status !== 'inserted' ? (
                  <AgentPassProgress rows={rows} run={documentImport.extraction_result?.agent} />
                ) : null}
                {documentImport.status !== 'inserted' && warnings.length ? (
                  <div className="flex flex-wrap items-start gap-x-4 gap-y-1.5 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                    <p className="flex min-w-0 flex-1 gap-2">
                      <AlertTriangle aria-hidden="true" className="mt-px size-3.5 shrink-0 text-amber-600" />
                      <span>{warnings.join(' · ')}</span>
                    </p>
                    <label className="flex shrink-0 cursor-pointer items-center gap-1.5 font-medium has-disabled:cursor-not-allowed has-disabled:opacity-60">
                      <input
                        checked={globalWarningsResolved}
                        className="size-3.5 accent-emerald-700"
                        disabled={locked}
                        onChange={(event) => {
                          setGlobalWarningsResolved(event.target.checked)
                          persist([], {}, { warnings_reviewed: event.target.checked })
                        }}
                        type="checkbox"
                      />
                      Reviewed
                    </label>
                  </div>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b bg-muted/40 px-4 py-2.5">
                <label className="relative block w-full sm:w-64">
                  <span className="sr-only">Find a source row</span>
                  <Search aria-hidden="true" className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <SearchInput
                    className="h-8 bg-background pl-8"
                    onChange={(event) => {
                      setRowQuery(event.target.value)
                      setVisibleRows(12)
                    }}
                    placeholder="Find item or model"
                    value={rowQuery}
                  />
                </label>
                {documentImport.status !== 'inserted' ? (
                  <div aria-label="Replace all with" className="flex flex-wrap items-center gap-1.5 lg:ml-auto" role="group">
                    <span className="text-xs font-medium text-muted-foreground">Replace all with:</span>
                    {alternativeChoices.map((choice) => (
                      <Button className="h-7 px-2 text-xs" disabled key={choice.value} size="sm" type="button" variant="outline">
                        {choice.label}
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>
              {displayedRows.map((row) => (
                <ReviewRow
                  accessories={rowAccessories[row.row_id] ?? []}
                  canInclude={canInsert}
                  configuration={rowOptions[row.row_id] ?? {}}
                  decision={decisions[row.row_id]}
                  disabled={locked || documentImport.status === 'inserted'}
                  importId={documentImport.id}
                  key={row.row_id}
                  manualMatch={manualMatches[row.row_id]}
                  onConfigure={(configuration) => {
                    const nextOptions = { ...rowOptions, [row.row_id]: configuration }
                    setRowOptions(nextOptions)
                    persist([row.row_id], { rowOptions: nextOptions })
                  }}
                  onDecide={(decision) => decide(row, decision)}
                  onManualMatch={(product) => {
                    const nextManual = { ...manualMatches, [row.row_id]: product }
                    setManualMatches(nextManual)
                    chooseProduct(row.row_id, product.id, { manualMatches: nextManual })
                  }}
                  onMatch={(productId) => chooseProduct(row.row_id, productId)}
                  onResolve={() => {
                    const nextResolved = { ...resolvedWarnings, [row.row_id]: !resolvedWarnings[row.row_id] }
                    setResolvedWarnings(nextResolved)
                    persist([row.row_id], { resolvedWarnings: nextResolved })
                  }}
                  onToggleAccessory={(productId) => {
                    const added = rowAccessories[row.row_id] ?? []
                    const nextAccessories = {
                      ...rowAccessories,
                      [row.row_id]: added.includes(productId) ? added.filter((id) => id !== productId) : [...added, productId],
                    }
                    setRowAccessories(nextAccessories)
                    persist([row.row_id], { rowAccessories: nextAccessories })
                  }}
                  row={row}
                  selectedProductId={matches[row.row_id]}
                  warningResolved={resolvedWarnings[row.row_id] ?? false}
                />
              ))}
              {displayedRows.length < filteredRows.length ? (
                <div className="border-t px-4 py-3 text-center">
                  <Button onClick={() => setVisibleRows((current) => current + 12)} size="sm" type="button" variant="outline">
                    Show 12 more
                  </Button>
                </div>
              ) : null}
            </SurfaceCard>
          )}
        </div>

        {!sourceName ? (
          <aside className="order-first space-y-4 xl:sticky xl:top-20 xl:order-last">
            <SurfaceCard title="Import settings">
              <div className="space-y-4 p-4">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Document type</span>
                  <Select
                    disabled={busy !== null}
                    onValueChange={(value) => {
                      setMode(value as ImportMode)
                      setStrategy(modeConfig[value as ImportMode].strategies[0]!.value)
                    }}
                    value={mode}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(modeConfig) as ImportMode[]).map((value) => (
                        <SelectItem key={value} value={value}>
                          {modeConfig[value].title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Match mode</span>
                  <Select disabled={busy !== null} onValueChange={(value) => setStrategy(value as MatchStrategy)} value={strategy}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {config.strategies.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              </div>
            </SurfaceCard>
          </aside>
        ) : null}
      </div>
    </section>
  )
}
